import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Choral Staves (Riven Choir's signature) — the arena becomes the sheet
 * music. Five horizontal voice lines span the arena; on each pulse of the
 * song, every line SINGS except a couple of resting ones — standing inside
 * a singing line's band when the pulse lands takes the hit. Dodging is
 * reading: each pulse's rests are drawn brighter-vs-dimmer through a
 * per-pulse wind-up, so the skill is sight-reading which staves stay
 * silent and sliding to one between beats. Phase 1+ adds a pulse; phase 2+
 * widens the bands.
 */
export class ChoralStaves extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Choral Staves', telegraphDuration: 1.2, activeDuration: 3.9, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(boss) {
    this._lineYs = [];
    for (let i = 0; i < this.config.lines; i++) {
      this._lineYs.push(ARENA.y + (ARENA.height * (i + 1)) / (this.config.lines + 1));
    }
    this._pulseCount = boss.phase >= 1 ? 4 : 3;
    this._bandHalf = this.config.bandHalf + (boss.phase >= 2 ? 4 : 0);
    this._pulseIndex = -1;
    this._pulseClock = 0;
    this._rests = new Set();
    this._pulseHitLatch = false;
    this._advancePulse();
  }

  _advancePulse() {
    this._pulseIndex++;
    this._pulseClock = 0;
    this._pulseHitLatch = false;
    this._rests = new Set();
    while (this._rests.size < this.config.restCount) {
      this._rests.add(Math.floor(Math.random() * this.config.lines));
    }
  }

  // Each pulse: 0.6s wind-up (rests readable), then a 0.3s sing window.
  static WINDUP = 0.6;
  static SING = 0.3;

  onActiveTick(dt, _boss, player, _pool) {
    if (this._pulseIndex >= this._pulseCount) return;
    this._pulseClock += dt;
    const singing = this._pulseClock >= ChoralStaves.WINDUP;

    if (singing && !this._pulseHitLatch) {
      for (let i = 0; i < this._lineYs.length; i++) {
        if (this._rests.has(i)) continue;
        if (Math.abs(player.y - this._lineYs[i]) < this._bandHalf + player.radius) {
          // One hit per pulse, however long they linger in the band —
          // the pulse is a beat, not a damage-over-time floor.
          player.takeDamage(this.config.pulseDamage);
          this._pulseHitLatch = true;
          break;
        }
      }
    }

    if (this._pulseClock >= ChoralStaves.WINDUP + ChoralStaves.SING) this._advancePulse();
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    const inPulse = this.state === PatternState.ACTIVE && this._pulseIndex < this._pulseCount;
    return {
      kind: 'staves',
      bandHalf: this._bandHalf,
      singing: inPulse && this._pulseClock >= ChoralStaves.WINDUP,
      windup: inPulse ? Math.min(1, this._pulseClock / ChoralStaves.WINDUP) : 0,
      lines: this._lineYs.map((y, i) => ({ y, resting: this._rests.has(i) })),
    };
  }
}
